import { QuestionAnswer } from "../-components/question-answer"
import { useEffect, useState } from "react"
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"

export const Route = createFileRoute(
  "/(app)/app/quizzes/$quizStateId/rounds/$roundNumber"
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { quizStateId, roundNumber } = Route.useParams()
  const navigate = useNavigate()
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false)

  const markRoundAnswers = useMutation(api.quizState.handlers.markRoundAnswers)
  const moveToNextRound = useMutation(api.quizState.handlers.moveToNextRound)
  const expireRoundTimer = useMutation(api.quizState.handlers.expireRoundTimer)
  const isRoundComplete = useQuery(
    api.quizState.handlers.isRoundComplete,
    quizStateId ? { quizStateId: quizStateId as Id<"quizState"> } : "skip"
  )

  // Fetch current user
  const currentUser = useQuery(api.users.handlers.current)

  // Fetch quiz state
  const quizStateData = useQuery(api.quizState.handlers.getQuizStateById, {
    quizStateId: quizStateId as Id<"quizState">,
  })

  // Fetch questions for this quiz state and round
  const questions = useQuery(
    api.quizzesToQuestions.handlers.getQuestionsByQuizStateAndRound,
    {
      quizStateId: quizStateId as Id<"quizState">,
      roundNumber: parseInt(roundNumber),
    }
  )

  // Fetch round scores to display correct/total counter
  const roundScores = useQuery(api.quizState.handlers.getRoundScores, {
    quizStateId: quizStateId as Id<"quizState">,
  })
  const currentRoundScore = roundScores?.find(
    (r) => r.roundNumber === parseInt(roundNumber)
  )

  const quizState = quizStateData?.quizState
  const quiz = quizStateData?.quiz

  // Fetch team if this is a team quiz
  const team = useQuery(
    api.teams.handlers.getTeam,
    quizState?.teamId ? { teamId: quizState.teamId } : "skip"
  )

  // Check if current user is the team captain (only applicable for team quizzes)
  const isTeamQuiz = quiz?.type === "teams" || quiz?.type === "pairs"
  const isCaptain = !!(
    currentUser &&
    team &&
    team.captainId === currentUser._id
  )
  // For team quizzes, only captain can submit. For individual quizzes, anyone can submit.
  const canSubmit = !isTeamQuiz || isCaptain

  // Timer logic
  useEffect(() => {
    if (!quizState?.roundStartedAt || quizState.status !== "in-progress") {
      setTimeRemaining(null)
      return
    }

    const updateTimer = () => {
      const startTime = new Date(quizState.roundStartedAt ?? "").getTime()
      const duration = quizState.roundDuration // Already in milliseconds
      const now = Date.now()
      const elapsed = now - startTime
      const remaining = Math.max(0, duration - elapsed)

      setTimeRemaining(Math.ceil(remaining / 1000))

      // Auto-mark answers when timer expires
      if (remaining <= 0 && !isAutoSubmitting && quizState.currentRound === parseInt(roundNumber)) {
        setIsAutoSubmitting(true)
        // First set to idle, then mark answers
        void expireRoundTimer({ quizStateId: quizState._id }).then(() => {
          void markRoundAnswers({ quizStateId: quizState._id })
        })
      }
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [quizState, isAutoSubmitting, markRoundAnswers, expireRoundTimer])

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!quizState || isSubmitting) return
    setIsSubmitting(true)
    try {
      await markRoundAnswers({ quizStateId: quizState._id })
    } catch (error) {
      console.error("Failed to mark round answers:", error)
      setIsSubmitting(false) // Only reset on error; success navigates away
    }
  }

  const handleNextRound = async () => {
    if (!quizState) return
    try {
      const result = await moveToNextRound({ quizStateId: quizState._id })
      window.scrollTo({ top: 0, behavior: "smooth" })
      setIsSubmitting(false) // Reset before navigating
      if (result.quizComplete) {
        void navigate({
          to: "/app/quizzes/$quizStateId",
          params: { quizStateId: quizState._id },
        })
      } else {
        void navigate({
          to: "/app/quizzes/$quizStateId/rounds/$roundNumber",
          params: {
            quizStateId: quizState._id,
            roundNumber: String(result.nextRound ?? 1),
          },
        })
      }
    } catch (error) {
      console.error("Failed to move to next round:", error)
    }
  }

  const handleReturnToLobby = () => {
    void navigate({
      to: "/app/quizzes/$quizStateId",
      params: { quizStateId },
    })
  }

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${String(mins)}:${String(secs).padStart(2, "0")}`
  }

  if (!quizState || !quiz || !questions) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  // Auto-navigate to new round when captain moves to next round
  // This handles team members who are on the completed round page
  if (
    quizState.status === "in-progress" &&
    quizState.currentRound !== parseInt(roundNumber)
  ) {
    return (
      <Navigate
        to="/app/quizzes/$quizStateId/rounds/$roundNumber"
        params={{
          quizStateId: quizState._id,
          roundNumber: quizState.currentRound.toString(),
        }}
      />
    )
  }

  const roundComplete = isRoundComplete ?? false
  const isInProgress = quizState.status === "in-progress"
  const roundDurationSeconds = quizState.roundDuration / 1000 // Convert to seconds for progress calculation
  const progress =
    timeRemaining !== null && roundDurationSeconds > 0
      ? ((roundDurationSeconds - timeRemaining) / roundDurationSeconds) * 100
      : 0

  return (
    <div className="container mx-auto max-w-4xl space-y-6 p-6">
      {/* Header with timer */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Round {roundNumber}</h1>
            <p className="text-muted-foreground">{quiz.name}</p>
          </div>
          {isInProgress && timeRemaining !== null && (
            <div className="text-right">
              <div className="text-3xl font-mono font-bold">
                {formatTime(timeRemaining)}
              </div>
              <div className="text-sm text-muted-foreground">remaining</div>
            </div>
          )}
        </div>
        {currentRoundScore?.status === "completed" && (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-lg px-4 py-2 font-mono">
              {currentRoundScore.correct} / {currentRoundScore.total}
            </Badge>
            <span className="text-sm text-muted-foreground">correct</span>
          </div>
        )}

        {/* Progress bar */}
        {isInProgress && timeRemaining !== null && (
          <Progress value={progress} className="h-2" />
        )}
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {questions.map((question, index) => (
          <div key={question._id} className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">
              Question {index + 1} of {questions.length}
            </div>
            <QuestionAnswer question={question} quizState={quizState} />
          </div>
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex gap-4 justify-end border-t pt-6">
        {isInProgress && quizState.status !== "idle" && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  onClick={() => void handleSubmit()}
                  size="lg"
                  disabled={!canSubmit || isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit Round"}
                </Button>
              </span>
            </TooltipTrigger>
            {!canSubmit && (
              <TooltipContent>
                Only the team captain can submit the round
              </TooltipContent>
            )}
          </Tooltip>
        )}
        {quizState.status === "idle" && !roundComplete && (
          <div className="text-sm text-muted-foreground">
            Time expired - marking answers...
          </div>
        )}
        {roundComplete && (
          <>
            <Button variant="outline" onClick={handleReturnToLobby}>
              Return to Lobby
            </Button>
            {quizState.currentRound < quiz.totalRounds && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      onClick={() => {
                        void handleNextRound()
                      }}
                      size="lg"
                      disabled={!canSubmit}
                    >
                      Next Round
                    </Button>
                  </span>
                </TooltipTrigger>
                {!canSubmit && (
                  <TooltipContent>
                    Only the team captain can start the next round
                  </TooltipContent>
                )}
              </Tooltip>
            )}
            {quizState.currentRound >= quiz.totalRounds && (
              <Button onClick={handleReturnToLobby} size="lg">
                View Results
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
